import { formatDate } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { ModalController, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { ColumnMode, DatatableComponent } from "@swimlane/ngx-datatable";
import { Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { LogicLog } from "src/model/class/general classes/logic-log";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { Settings } from "src/model/class/settings";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { iConnToolbarColour } from "src/components/shared-components/interfaces/iConnToolbarColour";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { LogicsService } from "src/service/logics.service";
import { NetworkService } from "src/service/network.service";
import { SecuretyDataProtectionService } from "src/service/securety-data-protection.service";
import { TranslationService } from "src/service/translation.service";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { serverSettings } from "src/state/selectors/server-settings-selector";

@Component({
    selector: "app-general-info-popup-alarm",
    templateUrl: "./general-info-popup-alarm.component.html",
    styleUrls: ["./general-info-popup-alarm.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GeneralInfoPopupAlarmComponent
  implements OnInit, OnDestroy, OnChanges, iConnToolbarColour, iTranslation {
  @Input() selectedAlarm: LogicLog = null;
  @Input() duplicateAlarms: Array<LogicLog> = Array<LogicLog>();
  @Input() otherAlarmsPresent: boolean = false;
  @Output() solveViewEmitter = new EventEmitter<string>();

  @ViewChild("table", { static: false }) table: DatatableComponent;

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectServerSettingsUserSub: Subscription = null;
  private _selectCurrentUserSub: Subscription = null;

  private _networkServiceSubscription: Subscription = null;
  private _translateSubsctiption: Subscription = null;

  private _currentUser: any = null;

  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public ColumnMode = ColumnMode;
  public description: string = "";
  public timestamp: string = "";
  public dateString: string = "";
  public timeString: string = "";
  public utcOffset: number = 0;
  public toolBarColor: string = "primary";
  public buttonViewCommandVisible: boolean = false;
  public buttonResetCommandVisible: boolean = false;
  public gridVisible: boolean = false;
  public isApp: boolean = false;

  constructor(
    public translationService: TranslationService,
    private _networkService: NetworkService,
    private _store: Store,
    private _modalController: ModalController,
    private _translateService: TranslateService,
    private _logicsService: LogicsService,
    private _securetyDataProtectionService: SecuretyDataProtectionService,
    private _platform: Platform
  ) {
    this.isApp = this._platform.is("ios") || this._platform.is("android");
  }

  ngOnInit(): void {
    this.subscribeNetworkServiceData();
    this.subscribeToSeverSettings();
    this.loadTranslation();
    this.subscribeTranslationEvent();
  }

  ngOnChanges(changes: any) {
    if (changes.selectedAlarm) {
      this.selectedAlarm = changes.selectedAlarm.currentValue;
    }
    if (changes.duplicateAlarms) {
      this.duplicateAlarms = [];
      this.duplicateAlarms = [...changes.duplicateAlarms.currentValue];
    }
    this.recalcTable();
  }

  ngOnDestroy(): void {
    this._networkServiceSubscription?.unsubscribe();
    this._translateSubsctiption?.unsubscribe();
    this.unsubscribeStoreSelect();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.description",
        "dictionary.dateAndTime",
        "dictionary.date",
        "dictionary.time",
      ])
      .subscribe((translation) => {
        this.description = translation["dictionary.description"];
        this.timestamp = translation["dictionary.dateAndTime"];
        this.dateString = translation["dictionary.date"];
        this.timeString = translation["dictionary.time"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubsctiption = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * At the click it allows you to view the alarm.
   */
  public async viewClick() {
    if (this.otherAlarmsPresent) {
      this._logicsService.ViewAlarm(
        this.selectedAlarm.idbigLogic,
        this._currentUser
      );
      this.solveViewEmitter.emit("VIEW");
    } else {
      await this._modalController.dismiss({
        operation: "VIEW",
        idLogic: this.selectedAlarm.idbigLogic,
      });
    }
  }

  /**
   * On click resets the alarm.
   */
  public async solveClick() {
    if (this.otherAlarmsPresent) {
      this._logicsService.SolveAlarm(
        this.selectedAlarm.idbigLogic,
        this._currentUser
      );
      this.solveViewEmitter.emit("RESET");
    } else {
      await this._modalController.dismiss({
        operation: "RESET",
        idLogic: this.selectedAlarm.idbigLogic,
      });
    }
  }

  /**
   * Returns the formatted date.
   */
  public formatDate(date: string) {
    return formatDate(
      date,
      "mediumDate",
      this.translationService.currentLanguage
    );
  }

  /**
   * Returns the formatted time.
   */
  public formatTime(date: string) {
    return formatDate(
      date,
      "mediumTime",
      this.translationService.currentLanguage
    );
  }

  /**
   * Recalculates the size of the table.
   */
  private recalcTable(): void {
    var timer = setTimeout(() => {
      if (this.table) {
        this.table.recalculate();
      }
      clearTimeout(timer);
    }, 100);
  }

  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  private subscribeToSeverSettings(): void {
    this._selectServerSettingsUserSub = this._store
      .select(serverSettings)
      .subscribe((settings: Settings) => {
        this.buttonViewCommandVisible =
          String(
            settings.Notification_ShowViewCommandsButtonIntoNotificationPopupWindow
          ).toLowerCase() == "true";
        this.buttonResetCommandVisible =
          String(
            settings.Notification_ShowResetCommandsButtonIntoNotificationPopupWindow
          ).toLowerCase() == "true";
        this.gridVisible =
          String(
            settings.Notification_ShowGridIntoNotificationPopupWindow
          ).toLowerCase() == "true";
      });
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData.currentUser)
      )
      .subscribe((user: CurrentSessionData) => {
        this._currentUser = this._securetyDataProtectionService.decypt(
          user.username
        );
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._selectServerSettingsUserSub?.unsubscribe();
    this._selectCurrentUserSub?.unsubscribe();
  }
  //#endregion
}
